import {loadPosterTemplateFromHashId} from '@PosterWhiteboard/poster/poster-init';
import {applyUpdatesBetweenIndices, updateGridItem} from '@Components/base-grid';
import {number} from 'prop-types';

export const TEMPLATES_GRID_ID = 'templates-grid';
export const RECOMMENDED_TEMPLATES_GRID_ID = 'recommended-templates-grid';
export const TEMPLATES_GRID_ITEM_HEIGHT_THRESHOLD = 80;
export const TEMPLATE_SEEN_TIMEOUT = 2000;
export const TEMPLATE_GRID_ITEM_DEFAULT_WEB_WIDTH = 145;
export const TEMPLATE_GRID_BATCH_SIZE = 50;

export const DEFUALT_TEMPLATE_SEEN_URI = 'poster/seen';

export enum TemplateOrientationTypes {
  Landscape = 'landscape',
  Portrait = 'portrait',
  Square = 'square',
}

interface PosterTemplateData {
  hashedID: string;
  isVideo: boolean;
  variantOfTemplateId: number;
  idcreator: number;
  width: number;
  height: number;
  previewURLs: string[];
  videoPreviewURLs: string[];
}

export interface SearchObject {
  batchNumber: number;
  batchOffset: number;
  posters: PosterTemplateData[];
}

export interface TemplateFilterOptionData {
  id: number;
  displayName: string;
  filterOption: string;
  colorHex?: string;
}
export interface TemplateFilterData {
  displayName: string;
  isColorFilter: boolean;
  iconName: string;
  optionsData: TemplateFilterOptionData[];
}

export const ALL_FILTER_ID = 'all';

export type TemplatesFiltersData = Record<string, TemplateFilterData>;

const handleTemplateViews = (hashId: string): void => {
  const gridData = window.PMW.redux.store.getState().grids.gridsHashmap[TEMPLATES_GRID_ID].items;

  if (!gridData?.length) {
    return;
  }

  const indexOfSelectedItem = gridData.findIndex((item) => item.id === hashId);
  if (indexOfSelectedItem === -1) {
    return;
  }

  if (!('idcreator' in gridData[indexOfSelectedItem].data && 'variantOfTemplateId' in gridData[indexOfSelectedItem].data)) {
    return;
  }

  if ('isDataIncremented' in gridData[indexOfSelectedItem].data && gridData[indexOfSelectedItem].data.isDataIncremented) {
    return;
  }

  const idCreatorTemplate = gridData[indexOfSelectedItem].data.idcreator as number;
  const idTemplate = gridData[indexOfSelectedItem].data.variantOfTemplateId as number;
  const itemsBefore = gridData.slice(0, indexOfSelectedItem);

  const idsOfItemsNotViewed: number[] = [];
  let startingIndex: number | null = 0;

  for (let i = 0; i < itemsBefore.length; i++) {
    const item = itemsBefore[i];
    if ('variantOfTemplateId' in item.data && 'isViewed' in item.data && !item.data.isViewed) {
      idsOfItemsNotViewed.push(item.data.variantOfTemplateId as number);

      if (startingIndex === null) {
        startingIndex = i;
      }
    }
  }

  window.PMW.incrementPosterView(idTemplate, idCreatorTemplate, idsOfItemsNotViewed, 'sizes');
  updateIsViewedFlag(startingIndex, indexOfSelectedItem);
  updateIncrementedFlag(gridData[indexOfSelectedItem].id);
};

const updateIncrementedFlag = (itemId: string): void => {
  window.PMW.redux.store.dispatch(
    updateGridItem({
      gridId: TEMPLATES_GRID_ID,
      itemId: itemId,
      changes: {
        data: {
          isDataIncremented: true,
        },
      },
    })
  );
};

const updateIsViewedFlag = (startIndex: number, endIndex: number): void => {
  window.PMW.redux.store.dispatch(
    applyUpdatesBetweenIndices({
      gridId: TEMPLATES_GRID_ID,
      startIndex,
      endIndex,
      changes: {
        data: {
          isViewed: true,
        },
      },
    })
  );
};

const updateIsSeenFlagInRedux = (startIndex: number, endIndex: number): void => {
  window.PMW.redux.store.dispatch(
    applyUpdatesBetweenIndices({
      gridId: TEMPLATES_GRID_ID,
      startIndex,
      endIndex,
      changes: {
        data: {
          isSeen: true,
        },
      },
    })
  );
};

const updateIsSeenFlag = (startIndex: number, endIndex: number): void => {
  const gridData = window.PMW.redux.store.getState().grids.gridsHashmap[TEMPLATES_GRID_ID].items;

  if (!gridData?.length) {
    return;
  }

  const templateIds: number[] = [];

  for (let i = startIndex; i < Math.min(gridData.length, endIndex); i++) {
    const itemData: Record<string, unknown> = gridData[i].data;
    if (itemData && 'isSeen' in itemData && 'variantOfTemplateId' in itemData && itemData.isSeen === false) {
      templateIds.push(itemData.variantOfTemplateId as number);
    }
  }

  window.PMW.incrementPosterSeenStat(templateIds);
  updateIsSeenFlagInRedux(startIndex, endIndex);
};

export const findAndIncrementSeenTemplates = (element: HTMLDivElement | null, gridItemHeight: number | null, gridItemWidth: number, upperOffset: number): void => {
  if (!element || !gridItemHeight) {
    return;
  }
  const verticalPadding = 16;
  const gap = 8;
  const seenThreshold = 0.8;
  const numberOfItemsInARow = Math.floor(element.getBoundingClientRect().width / gridItemWidth);
  let numberOfVerticalItems = (element.clientHeight + element.scrollTop - verticalPadding - upperOffset) / (gridItemHeight + gap);

  if (numberOfVerticalItems < 1) {
    return;
  }

  if (numberOfVerticalItems - Math.floor(numberOfVerticalItems) > seenThreshold) {
    numberOfVerticalItems = Math.ceil(numberOfVerticalItems);
  } else {
    numberOfVerticalItems = Math.floor(numberOfVerticalItems);
  }

  const totalNumberOfItems = numberOfVerticalItems * numberOfItemsInARow;
  updateIsSeenFlag(0, totalNumberOfItems);
  updateIsSeenFlagInRedux(0, totalNumberOfItems);
};

export const onClickTemplateItem = async (hashId: string): Promise<void> => {
  handleTemplateViews(hashId);
  await loadPosterTemplateFromHashId(hashId);
};

export const isTemplateGridHeightBelowThreshold = (gridItemHeight: number | null): boolean => {
  if (gridItemHeight) {
    return gridItemHeight < TEMPLATES_GRID_ITEM_HEIGHT_THRESHOLD;
  }
  return false;
};

export const getFiltersForTemplatesInEditor = async (): Promise<TemplatesFiltersData> => {
  return (await window.PMW.readLocal('posters/getFiltersForEditorDisplay')) as TemplatesFiltersData;
};

export const getDataForTemplates = async (params: unknown): Promise<SearchObject> => {
  return (await window.PMW.readLocal('posters/editorSearch', params)) as SearchObject;
};

export const getRecommendedDataForTemplates = async (params: unknown): Promise<SearchObject> => {
  return (await window.PMW.readLocal('posters/editorTemplatesYouMayLike', params)) as SearchObject;
};
